import { useCallback, useEffect, useState } from "react";
import accountService from "../Services/accountService";
import authService from "../Services/authService";

// Who a purchase is for: the name and email the payment provider puts on the
// receipt. Prefilled from what we already know (saved payment email, the
// account's own email and name, the player name) so most buyers never type
// anything; asked for inline only when something is missing.
const NAME_KEY = "nameit_payer_name";

export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

function readLocal(key) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeLocal(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage can be blocked; the details just won't be remembered next time.
  }
}

function problemWith(name, email) {
  if (name.trim().length < 2) return "Add the name for your receipt.";
  if (!email.trim()) return "Add an email for your receipt.";
  if (!isValidEmail(email)) return "That email doesn't look right. Check it and try again.";
  return "";
}

export default function usePayerDetails(active = true) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    (async () => {
      const user = authService.auth.currentUser;
      const saved = (await accountService.getSavedEmail().catch(() => null)) || "";
      if (cancelled) return;
      const nextEmail = saved || user?.email || "";
      const nextName = readLocal(NAME_KEY) || user?.displayName || readLocal("playerName");
      setSavedEmail(saved);
      setEmail(nextEmail);
      setName(nextName);
      setError("");
      // Only open the fields when we genuinely lack something.
      setEditing(!!problemWith(nextName, nextEmail));
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [active]);

  const update = useCallback((field, value) => {
    setError("");
    if (field === "name") setName(value);
    else setEmail(value);
  }, []);

  /**
   * Validates and remembers the details. Returns { name, email } when they are
   * good to pay with, or null (with `error` set and the fields open) when not.
   */
  const commit = useCallback(async () => {
    const problem = problemWith(name, email);
    if (problem) {
      setError(problem);
      setEditing(true);
      return null;
    }
    const clean = { name: name.trim(), email: email.trim() };
    writeLocal(NAME_KEY, clean.name);
    if (clean.email !== savedEmail) {
      // Best effort: a failed save must never block the purchase itself.
      try {
        await accountService.saveEmail(clean.email);
        setSavedEmail(clean.email);
      } catch (err) {
        console.warn("Could not save payment email:", err);
      }
    }
    setName(clean.name);
    setEmail(clean.email);
    setEditing(false);
    return clean;
  }, [name, email, savedEmail]);

  return {
    name,
    email,
    loaded,
    editing,
    error,
    complete: !problemWith(name, email),
    setName: (value) => update("name", value),
    setEmail: (value) => update("email", value),
    startEditing: () => setEditing(true),
    commit,
  };
}
